import { Link, useNavigate } from "react-router-dom";
import { Check, Star, Crown, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import Seo from "../components/Seo";

const PKGS = [
    {
        tier: "Bronze",
        subtitle: "Erste Analyse",
        icon: Sparkles,
        features: [
            "Erste Einschätzung der Vermögenssituation",
            "Grobe Einordnung der Pflegeheimkosten",
            "Erster Überblick über mögliche Wege",
            "Geeignet für schnelle Erstorientierung",
        ],
        color: "from-amber-100 to-amber-50",
        border: "border-amber-200",
        highlight: false,
    },
    {
        tier: "Silber",
        subtitle: "Detaillierte Prüfung",
        icon: Star,
        features: [
            "Vertiefte Analyse von Einkommen und Vermögen",
            "Klarere Risikoeinordnung",
            "Detailprüfung der Heimkosten",
            "Geeignet für komplexere oder unsichere Fälle",
        ],
        color: "from-brand-100 to-brand-50",
        border: "border-brand-300",
        highlight: true,
    },
    {
        tier: "Gold",
        subtitle: "Strategische Begleitung",
        icon: Crown,
        features: [
            "Begleitung bei Gesprächen mit Heim, Kasse, Behörden",
            "Übernahme der Abstimmung mit Beteiligten",
            "Belastung für Angehörige bestmöglich reduzieren",
            "Besonders relevant bei Einkommen über 100.000 €",
        ],
        color: "from-yellow-100 to-amber-50",
        border: "border-yellow-300",
        highlight: false,
    },
];

export default function PackagesPage() {
    const navigate = useNavigate();
    return (
        <div className="py-12 sm:py-16">
            <Seo
                title="Beratungspakete Bronze, Silber & Gold"
                description="Strukturierte Beratung zu Pflegeheimkosten und Elternunterhalt. Vom kompakten Erstüberblick (Bronze) bis zur strategischen Begleitung bei Heim, Kasse und Behörden (Gold). Individuelle Empfehlung nach Ihrer Situation."
                path="/pakete"
            />
            <div className="container">
                <div className="max-w-2xl">
                    <div className="text-xs font-semibold uppercase tracking-wider text-brand-600">Beratungspakete</div>
                    <h1 className="mt-2 font-display text-4xl font-bold tracking-tight sm:text-5xl">
                        Vom ersten Überblick bis zur strategischen Begleitung.
                    </h1>
                    <p className="mt-3 text-ink-500">
                        Wählen Sie das Paket, das zu Ihrer Situation passt. Wir empfehlen automatisch die
                        passende Stufe – Sie entscheiden.
                    </p>
                </div>

                <div className="mt-10 grid gap-6 md:grid-cols-3">
                    {PKGS.map((p) => (
                        <div
                            key={p.tier}
                            className={`relative rounded-3xl border-2 ${p.border} bg-gradient-to-br ${p.color} p-7 shadow-softer transition hover:-translate-y-1 ${
                                p.highlight ? "md:-translate-y-4 md:shadow-soft" : ""
                            }`}
                            data-testid={`package-${p.tier}`}
                        >
                            {p.highlight && (
                                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-brand-500 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-white shadow-soft">
                                    Empfohlen
                                </span>
                            )}
                            <div className="flex items-center gap-3">
                                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white text-brand-600">
                                    <p.icon className="h-5 w-5" />
                                </span>
                                <div>
                                    <div className="text-xs text-ink-500">{p.subtitle}</div>
                                    <div className="font-display text-2xl font-bold">{p.tier}</div>
                                </div>
                            </div>
                            <ul className="mt-5 space-y-2">
                                {p.features.map((f) => (
                                    <li key={f} className="flex items-start gap-2 text-sm text-ink-700">
                                        <Check className="mt-0.5 h-4 w-4 flex-none text-brand-600" />
                                        <span>{f}</span>
                                    </li>
                                ))}
                            </ul>
                            <Button
                                onClick={() => navigate("/kontakt", { state: { package: p.tier } })}
                                className={`mt-6 h-12 w-full rounded-full ${
                                    p.highlight
                                        ? "bg-brand-500 hover:bg-brand-600"
                                        : "bg-white text-ink-900 hover:bg-brand-50 border border-ink-300/30"
                                }`}
                                data-testid={`package-cta-${p.tier}`}
                            >
                                {p.tier} anfragen
                            </Button>
                        </div>
                    ))}
                </div>

                <div className="mt-12 rounded-3xl border border-ink-300/30 bg-white p-6 text-sm text-ink-500">
                    Kein Paket ist eine automatisierte Rechtsberatung. Preise und Umfang werden individuell im
                    Anschluss an eine unverbindliche Anfrage besprochen.
                    <Link to="/check" className="ml-2 font-semibold text-brand-600" data-testid="packages-check-link">
                        Noch keine Einschätzung? Jetzt kostenlosen Check starten →
                    </Link>
                </div>
            </div>
        </div>
    );
}
